import axios from "axios";

/** Notifications on this phone or computer: asking permission, joining (or leaving) the server's list, and keeping the two in step. */

export const pushSupported = () => typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window && window.isSecureContext !== false;
export const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export const isStandalone = () => (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || window.navigator.standalone === true;
export const permission = () => (typeof Notification === "undefined" ? "unsupported" : Notification.permission);

// Whether this device was turned on by its person (and not turned off since). Only a device that was turned on is quietly put right when the browser drops its subscription.
const WANT = "hp-push-wanted";
const wanted = () => { try { return localStorage.getItem(WANT) === "1"; } catch { return false; } };
const setWanted = (on) => { try { if (on) localStorage.setItem(WANT, "1"); else localStorage.removeItem(WANT); } catch { /* private window: nothing to remember */ } };

const keyBytes = (b64) => { const pad = "=".repeat((4 - (b64.length % 4)) % 4); const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/")); return Uint8Array.from([...raw].map((c) => c.charCodeAt(0))); };

async function registration() {
  const reg = await Promise.race([navigator.serviceWorker.ready, new Promise((_, rej) => setTimeout(() => rej(new Error("The app isn't ready for notifications yet. Reload the page and try again.")), 8000))]);
  return reg;
}

/** This device's current subscription, or null. */
export async function currentSubscription() {
  if (!pushSupported()) return null;
  try { return await (await registration()).pushManager.getSubscription(); } catch { return null; }
}

/** The browser's own reasons, in words a person can act on. */
export function explainPushError(e) {
  if (e && e.response) return null;                                  // a server answer: the caller shows the server's words
  const name = e && e.name, msg = (e && e.message) || "";
  if (name === "NotAllowedError") return "Notifications were blocked or the question was dismissed. Allow notifications for this app in your phone's settings, then try again.";
  if (name === "AbortError" || /push service/i.test(msg)) return "This phone couldn't reach its notification service (Google or Apple). Check the phone is online and try again in a minute. Phones without Google services can't receive web notifications.";
  if (name === "InvalidStateError") return "This device has an old notification setup that is stuck. Use \"Reset notifications on this device\" below.";
  if (name === "NotSupportedError" || name === "SecurityError") return "This browser can't receive notifications here. On an iPhone, add the app to the Home Screen first.";
  return msg || "Notifications couldn't be turned on.";
}

/** Throws away whatever subscription this device had and makes a new one. A subscription the push service no longer accepts (the server removes those) otherwise stays on the phone, looks "on", and never delivers. */
async function freshSubscription(reg, publicKey) {
  let replaces = "";
  try { const old = await reg.pushManager.getSubscription(); if (old) { replaces = old.endpoint; await old.unsubscribe(); } } catch { /* it may already be gone */ }
  const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
  return { sub, replaces };
}

async function tellServer(api, { sub, replaces }) {                 // `replaces` is the old address, so the server doesn't keep a dead one beside the new
  const j = sub.toJSON();
  await axios.post(`${api}/push/subscribe`, { endpoint: j.endpoint, keys: j.keys, userAgent: navigator.userAgent.slice(0, 200), replaces: replaces && replaces !== j.endpoint ? replaces : "" });
}

/** Asks the person's permission (only ever from a tap), subscribes this device afresh and tells the server. `publicKey` is the server's key from the panel, so nothing is waited for between the tap and the permission and subscribe calls (iPhones want those inside the tap). Throws when it can't: use explainPushError. */
export async function enablePush(api, publicKey) {
  if (!pushSupported()) throw new Error("This browser can't receive notifications.");
  const result = await Notification.requestPermission();
  if (result !== "granted") throw new Error(result === "denied" ? "Notifications are blocked for this site. Allow them in your browser or phone settings." : "Notifications weren't turned on.");
  const key = publicKey || (await axios.get(`${api}/push/config`)).data.publicKey;
  const made = await freshSubscription(await registration(), key);
  await tellServer(api, made);
  setWanted(true);
  return made.sub;
}

/** Turns notifications off for this device only. */
export async function disablePush(api) {
  setWanted(false);
  const sub = await currentSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  try { await axios.post(`${api}/push/unsubscribe`, { endpoint }); } catch { /* the server drops a dead address by itself */ }
  try { await sub.unsubscribe(); } catch { /* already gone */ }
}

/** On signing out: this phone must stop getting that person's notifications, whoever signs in next. Never blocks the sign-out. */
export async function forgetThisDevice(api) {
  try { if (permission() === "granted") await disablePush(api); } catch { /* ignore */ }
}

/** When the app opens signed in and this device already has permission: make sure the server has a working copy of this device's subscription. If the server's key changed, or the server no longer lists this device (it removes addresses the push service has stopped accepting), subscribe afresh (no prompt: permission was already given). */
export async function syncPush(api) {
  try {
    if (!pushSupported() || permission() !== "granted") return;
    const sub = await currentSubscription();
    if (!sub) {                                                       // the browser dropped it (they do, after a long time closed): permission is still given, so subscribe again without asking
      if (!wanted()) return;
      const { data } = await axios.get(`${api}/push/config`, { silent: true });
      await tellServer(api, await freshSubscription(await registration(), data.publicKey));
      return;
    }
    const { data } = await axios.get(`${api}/push/config`, { params: { endpoint: sub.endpoint }, silent: true });
    const have = sub.options?.applicationServerKey ? new Uint8Array(sub.options.applicationServerKey) : null;
    const want = keyBytes(data.publicKey);
    const sameKey = have && have.length === want.length && !have.some((v, i) => v !== want[i]);
    if (sameKey && data.thisDevice) { setWanted(true); return; }    // all in step: a device that is on remembers it, so it can put itself right later
    await tellServer(api, await freshSubscription(await registration(), data.publicKey));
    setWanted(true);
  } catch { /* try again next time */ }
}

/** "Reset notifications on this device": the same as turning off and on, in one go, for a device that says it is on but isn't getting anything. */
export async function resetPush(api, publicKey) {
  if (permission() !== "granted") throw new Error("Notifications are blocked for this site. Allow them in your browser or phone settings.");
  const made = await freshSubscription(await registration(), publicKey || (await axios.get(`${api}/push/config`)).data.publicKey);
  await tellServer(api, made);
  return made.sub;
}
